import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface CategoryFunction {
  id: string;
  name: string;
  code: string;
  route: string;
  icon?: string;
  menuOrder: number;
  isActive: boolean;
}

export interface FunctionCategory {
  id: string;
  name: string;
  code: string;
  description?: string;
  icon?: string;
  menuOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  functions?: CategoryFunction[];
}

export interface CreateFunctionCategoryDto {
  name: string;
  code: string;
  description?: string;
  icon?: string;
  menuOrder?: number;
  isActive?: boolean;
}

export interface UpdateFunctionCategoryDto {
  name?: string;
  code?: string;
  description?: string;
  icon?: string;
  menuOrder?: number;
  isActive?: boolean;
}

export interface FunctionCategoryImportResult {
  success: boolean;
  message: string;
  importedCount: number;
  updatedCount: number;
  skippedCount: number;
  skippedCodes: string[];
  missingFunctionCodes: string[];
  errors: string[];
  categories?: FunctionCategory[];
}

export interface FunctionCategoriesListResponse {
  categories: FunctionCategory[];
  usingTemplate: boolean;
}

export const functionCategoriesApi = {
  async getAllCategories(): Promise<FunctionCategoriesListResponse> {
    const response = await api.get('/function-categories');
    const data = response.data;
    if (Array.isArray(data)) {
      return { categories: data, usingTemplate: false };
    }
    return data;
  },

  async getCategoryById(id: string): Promise<FunctionCategory> {
    const response = await api.get(`/function-categories/${id}`);
    return response.data;
  },

  async createCategory(data: CreateFunctionCategoryDto): Promise<FunctionCategory> {
    const response = await api.post('/function-categories', data);
    return response.data;
  },

  async updateCategory(id: string, data: UpdateFunctionCategoryDto): Promise<FunctionCategory> {
    const response = await api.patch(`/function-categories/${id}`, data);
    return response.data;
  },

  async deleteCategory(id: string): Promise<void> {
    await api.delete(`/function-categories/${id}`);
  },

  async reorderCategories(items: { id: string; menuOrder: number }[]): Promise<FunctionCategory[]> {
    const response = await api.patch('/function-categories/reorder', { items });
    return response.data;
  },

  async reorderCategoryFunctions(categoryId: string, functionIds: string[]): Promise<FunctionCategory> {
    const response = await api.patch(`/function-categories/${categoryId}/functions/reorder`, { functionIds });
    return response.data;
  },

  async assignFunctionsToCategory(categoryId: string, functionIds: string[]): Promise<FunctionCategory> {
    const response = await api.post(`/function-categories/${categoryId}/functions`, { functionIds });
    return response.data;
  },

  async removeFunctionFromCategory(categoryId: string, functionId: string): Promise<FunctionCategory> {
    const response = await api.delete(`/function-categories/${categoryId}/functions/${functionId}`);
    return response.data;
  },

  async createDefaultCategories(): Promise<{ message: string; categories: FunctionCategory[] }> {
    const response = await api.post('/function-categories/seed');
    return response.data;
  },

  async exportCategories(): Promise<void> {
    const response = await api.get('/function-categories/export', {
      responseType: 'blob',
    });

    const disposition = response.headers['content-disposition'] as string | undefined;
    const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
    const filename =
      filenameMatch?.[1] ||
      `function-categories-export-${new Date().toISOString().slice(0, 10)}.json`;

    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  async importCategories(
    file: File,
    mode: 'skip' | 'replace' = 'skip',
  ): Promise<FunctionCategoryImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', mode);

    const response = await api.post('/function-categories/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};
